import api from './auth.api';

export interface ReminderAttempt {
  id: number;
  site_id: number;
  subscriber_id: number;
  invoice_id: number;
  reminder_run_id: number | null;
  message_type:
    | 'due_reminder'
    | 'overdue_reminder'
    | 'email_due_reminder'
    | 'upcoming_due'
    | 'thank_you';
  channel: 'whatsapp' | 'email' | null;
  source: 'auto' | 'manual' | null;
  status: 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'cancelled' | 'skipped';
  skip_reason: string | null;
  amount: string | null;
  due_date: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  failed_at: string | null;
  error_message: string | null;
  idempotency_key: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  subscriber_name?: string;
  subscriber_phone?: string;
  invoice_number?: string;
  invoice_due_date?: string;
  amount_outstanding?: string;
  site_name?: string;
  whatsapp_status?: string;
  provider_message_id?: string | null;
  provider_response?: unknown;
}

export interface ListRemindersResponse {
  success: boolean;
  data: {
    data: ReminderAttempt[];
    pagination: {
      page: number;
      perPage: number;
      total: number;
      totalPages: number;
    };
  };
}

export interface SendReminderResponse {
  success: boolean;
  data: {
    attemptId: number;
    whatsappMessageId?: number;
    dryRun: boolean;
    status: string;
    message: string;
    invoice: {
      id: number;
      invoiceNumber: string;
      amountOutstanding: number;
      dueDate: string;
      dueStatus: string;
    };
    subscriber: {
      id: number;
      name: string;
      phone: string;
    };
    provider?: {
      requestId?: string;
      messageId?: string;
    };
    messageParams?: unknown;
  };
}

export interface RetryReminderResponse {
  success: boolean;
  data: {
    originalAttemptId: number;
    newAttemptId: number;
    whatsappMessageId?: number;
    status: string;
    message: string;
  };
}

export interface CancelReminderResponse {
  success: boolean;
  data: {
    attemptId: number;
    status: string;
    message: string;
  };
}

export type ReminderCategory = 'upcoming_payment' | 'pending_dues';

export interface ChannelReminderSummary {
  lastSentAt: string | null;
  category: ReminderCategory | null;
}

export interface SubscriberReminderSummaryRow {
  subscriber: {
    id: number;
    fullname: string | null;
    subscriberCode: string | null;
    phone: string | null;
    email: string | null;
    reminderPaused: boolean;
  };
  pendingAmount: number;
  whatsapp: ChannelReminderSummary;
  email: ChannelReminderSummary;
}

export interface ListSubscriberReminderSummaryResponse {
  success: boolean;
  data: {
    data: SubscriberReminderSummaryRow[];
    pagination: {
      page: number;
      perPage: number;
      total: number;
      totalPages: number;
    };
  };
}

export const remindersApi = {
  // List reminder attempts with pagination and filters
  listReminders: async (params: {
    page?: number;
    perPage?: number;
    siteId?: number;
    subscriberId?: number;
    invoiceId?: number;
    status?: string;
    messageType?: string;
    channel?: string;
    source?: string;
    fromDate?: string;
    toDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ListRemindersResponse['data']> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.perPage) queryParams.set('perPage', String(params.perPage));
    if (params.siteId) queryParams.set('siteId', String(params.siteId));
    if (params.subscriberId) queryParams.set('subscriberId', String(params.subscriberId));
    if (params.invoiceId) queryParams.set('invoiceId', String(params.invoiceId));
    if (params.status) queryParams.set('status', params.status);
    if (params.messageType) queryParams.set('messageType', params.messageType);
    if (params.channel) queryParams.set('channel', params.channel);
    if (params.source) queryParams.set('source', params.source);
    if (params.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params.toDate) queryParams.set('toDate', params.toDate);
    if (params.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    const response = await api.get<ListRemindersResponse>(`/reminders?${queryParams.toString()}`);
    return response.data.data;
  },

  // Get single reminder attempt by ID
  getReminderAttempt: async (id: number): Promise<ReminderAttempt & { whatsappMessage?: unknown }> => {
    const response = await api.get<{ success: boolean; data: ReminderAttempt & { whatsappMessage?: unknown } }>(
      `/reminders/attempts/${id}`
    );
    return response.data.data;
  },

  // Send reminder for an invoice (manual send)
  sendReminder: async (invoiceId: number, dryRun: boolean = false): Promise<SendReminderResponse['data']> => {
    // Note: This uses the invoices endpoint for manual sends
    const response = await api.post<SendReminderResponse>(`/invoices/${invoiceId}/send-reminder-now`, {
      dryRun,
    });
    return response.data.data;
  },

  // Retry a failed reminder attempt
  retryReminder: async (attemptId: number): Promise<RetryReminderResponse['data']> => {
    const response = await api.post<RetryReminderResponse>(`/reminders/attempts/${attemptId}/retry`);
    return response.data.data;
  },

  // Cancel a queued reminder attempt
  cancelReminder: async (attemptId: number): Promise<CancelReminderResponse['data']> => {
    const response = await api.post<CancelReminderResponse>(`/reminders/attempts/${attemptId}/cancel`);
    return response.data.data;
  },

  listSubscriberReminderSummary: async (params: {
    page?: number;
    perPage?: number;
    search?: string;
    reminderCategory?: ReminderCategory;
    hasReminder?: boolean;
  }): Promise<ListSubscriberReminderSummaryResponse['data']> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.perPage) queryParams.set('per_page', String(params.perPage));
    if (params.search) queryParams.set('search', params.search);
    if (params.reminderCategory) queryParams.set('reminder_category', params.reminderCategory);
    if (params.hasReminder !== undefined) {
      queryParams.set('has_reminder', params.hasReminder ? 'true' : 'false');
    }

    const response = await api.get<ListSubscriberReminderSummaryResponse>(
      `/reminders/subscriber-summary?${queryParams.toString()}`
    );
    return response.data.data;
  },
};

export default remindersApi;
